import {useCallback, useEffect} from 'react';
import {UserProfile} from 'APIs/letrusAPI/user/types';
import {updateProfileService} from 'store/services/profileServices';
import {updateUserData} from 'utils/functions/getUserData/getUserData';

export interface ClosableDialog {
  addEventListener(type: 'close', listener: () => void): void;
  removeEventListener(type: 'close', listener: () => void): void;
}

// Persists the "seen" flag for the class-transfer notice (BO-2277,
// TRANSF-04/05) by listening to the dialog's native `close` event — this
// catches every way the modal can close (the "Entendi" button, the built-in
// close button, ESC), not just an explicit button click, without the modal
// component itself needing an internal ref: dialog control stays delegated
// to the parent via letrus-ui's useModals, per this project's convention.
export function useCloseTransferNotice(
  userProfile: UserProfile | undefined,
  modal: ClosableDialog | null | undefined,
) {
  const closeTransferNotice = useCallback(async () => {
    const transferId = userProfile?.recent_transfer_notice?.transfer_id;
    if (!transferId || !userProfile?.id) {
      return;
    }

    const seenFlag = `transfer_notice_seen:${transferId}`;
    const tourFlags = userProfile?.tour_flags ?? [];
    const updatedTourFlags = tourFlags.includes(seenFlag)
      ? tourFlags
      : [...tourFlags, seenFlag];

    try {
      await updateProfileService({
        id: userProfile.id,
        tour_flags: updatedTourFlags,
      });

      // Keeps the cached session (read on every reload by AuthRoute, which
      // only re-fetches /api/v1/me/ when there's no local session yet) in
      // sync — otherwise the modal would keep reappearing after a refresh
      // even though the flag is already saved server-side.
      updateUserData('tour_flags', updatedTourFlags);
      updateUserData('recent_transfer_notice', null);
    } catch (error) {
      console.error(error);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!modal) {
      return undefined;
    }

    modal.addEventListener('close', closeTransferNotice);

    return () => {
      modal.removeEventListener('close', closeTransferNotice);
    };
  }, [modal, closeTransferNotice]);
}

export default useCloseTransferNotice;
